/**
 * Thành phần giao diện dùng chung cho module Feedback (chuẩn EMS v1, mục 4).
 * Chip trạng thái/mức độ/loại, lớp bottom sheet cho dialog trên mobile, nút chọn ảnh
 * (mobile: Chụp ảnh + Chọn từ thư viện; desktop: Chọn ảnh), lưới thumbnail và phóng to ảnh.
 */
import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Camera, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { uploadApi } from "@/lib/api-client";
import {
  cn,
  getUploadUrl,
  validateUploadFile,
  MAX_MEDIA_IMAGE_SIZE,
  extractErrorMessage,
} from "@/lib/utils";
import {
  FEEDBACK_PRIORITY_LABELS,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_TYPE_LABELS,
} from "@/shared/schemas/feedback.schema";
import type {
  FeedbackPriority,
  FeedbackStatus,
  FeedbackType,
} from "@/shared/schemas/feedback.schema";

/**
 * DialogContent: mobile = bottom sheet toàn chiều rộng (cao tối đa 90vh, nội dung cuộn bên trong,
 * nút hành động dính đáy, tôn trọng safe-area); desktop = dialog giữa màn hình.
 * Dùng kèm FEEDBACK_SHEET_BODY_CLASS và FEEDBACK_SHEET_FOOTER_CLASS.
 */
export const FEEDBACK_SHEET_CONTENT_CLASS =
  "flex flex-col gap-0 p-0 overflow-hidden max-h-[90vh] sm:max-w-lg " +
  "max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 " +
  "max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:border-x-0 max-sm:border-b-0";
// Header popup phản hồi theo chuẩn header popup EMS (components/ui/dialog-standard.ts) — chỉ giữ căn trái
export const FEEDBACK_SHEET_HEADER_CLASS = "text-left";
export const FEEDBACK_SHEET_BODY_CLASS = "flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-5 space-y-4";
export const FEEDBACK_SHEET_FOOTER_CLASS =
  "flex flex-row gap-2 justify-end px-4 py-3 sm:px-5 border-t border-slate-100 bg-white pb-[max(0.75rem,env(safe-area-inset-bottom))]";

export const STATUS_CHIP_STYLES: Record<FeedbackStatus, string> = {
  OPEN: "bg-blue-50 text-blue-700 border-blue-200",
  IN_PROGRESS: "bg-amber-50 text-amber-800 border-amber-200",
  RESOLVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CLOSED: "bg-slate-100 text-slate-700 border-slate-300",
};
export const STATUS_DOT_STYLES: Record<FeedbackStatus, string> = {
  OPEN: "bg-blue-500",
  IN_PROGRESS: "bg-amber-500",
  RESOLVED: "bg-emerald-500",
  CLOSED: "bg-slate-400",
};
const PRIORITY_CHIP_STYLES: Record<FeedbackPriority, string> = {
  CRITICAL: "bg-red-50 text-red-700 border-red-200",
  HIGH: "bg-orange-50 text-orange-700 border-orange-200",
  MEDIUM: "bg-blue-50 text-blue-700 border-blue-200",
  LOW: "bg-slate-100 text-slate-600 border-slate-200",
};
const TYPE_CHIP_STYLES: Record<FeedbackType, string> = {
  BUG: "bg-rose-50 text-rose-700 border-rose-200",
  IMPROVEMENT: "bg-violet-50 text-violet-700 border-violet-200",
  QUESTION: "bg-sky-50 text-sky-700 border-sky-200",
};

const CHIP_BASE =
  "inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold whitespace-nowrap";

export function FeedbackStatusChip({ status, className }: { status: string; className?: string }) {
  const s = status as FeedbackStatus;
  return (
    <span data-testid="fb-status-chip" className={cn(CHIP_BASE, STATUS_CHIP_STYLES[s], className)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT_STYLES[s])} />
      {FEEDBACK_STATUS_LABELS[s] || status}
    </span>
  );
}

export function FeedbackPriorityChip({ priority, className }: { priority: string; className?: string }) {
  const p = priority as FeedbackPriority;
  return (
    <span className={cn(CHIP_BASE, PRIORITY_CHIP_STYLES[p], className)}>
      {FEEDBACK_PRIORITY_LABELS[p] || priority}
    </span>
  );
}

export function FeedbackTypeChip({ type, className }: { type: string; className?: string }) {
  const t = type as FeedbackType;
  return (
    <span className={cn(CHIP_BASE, TYPE_CHIP_STYLES[t], className)}>
      {FEEDBACK_TYPE_LABELS[t] || type}
    </span>
  );
}

export function FeedbackSourceChip({ source, className }: { source?: string | null; className?: string }) {
  if (!source || source === "USER") return null;
  if (source === "QA") {
    return (
      <span
        data-testid="fb-source-chip"
        className={cn(CHIP_BASE, "bg-teal-50 text-teal-700 border-teal-200", className)}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
        QA Tester
      </span>
    );
  }
  const label = source === "AUTO_SERVER" ? "Tự động (Server)" : "Tự động";
  return (
    <span
      data-testid="fb-source-chip"
      className={cn(CHIP_BASE, "bg-purple-50 text-purple-700 border-purple-200", className)}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
      {label}
    </span>
  );
}

export function FeedbackOccurrenceChip({
  occurrenceCount,
  affectedUsers,
  source,
  className,
}: {
  occurrenceCount?: number | null;
  affectedUsers?: number | null;
  source?: string | null;
  className?: string;
}) {
  const count = Number(occurrenceCount || 1);
  const users = Number(affectedUsers || 1);
  if (count <= 1 && (!source || source === "USER")) return null;
  return (
    <span
      data-testid="fb-occurrence-chip"
      className={cn(CHIP_BASE, "bg-slate-100 text-slate-700 border-slate-200 tabular-nums", className)}
    >
      ×{count} lần · {users} người
    </span>
  );
}

export function formatFeedbackDate(value?: string | Date | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "dd/MM/yyyy HH:mm");
}

/** Đọc mảng ảnh (Json) an toàn, vẫn đọc được dữ liệu cũ dạng chuỗi JSON */
export function parseFeedbackImages(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((x): x is string => typeof x === "string" && !!x);
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string" && !!x) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function useMediaQuery(query: string): boolean {
  const get = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [query]);
  return matches;
}

/** Thiết bị cảm ứng (ẩn chụp vùng màn hình, hiện Chụp ảnh / Chọn từ thư viện) */
export function useIsTouchDevice(): boolean {
  return useMediaQuery("(pointer: coarse)");
}

/** < 768px (breakpoint md) */
export function useIsMobileViewport(): boolean {
  return useMediaQuery("(max-width: 767px)");
}

/** Kiểm tra dung lượng rồi tải ảnh lên server (luôn lưu URL, không lưu base64) */
export async function uploadFeedbackImages(
  files: File[],
  remainingSlots: number,
  label = "Ảnh",
): Promise<string[]> {
  const urls: string[] = [];
  const imageFiles = files.filter((f) => f.type.startsWith("image/"));
  if (imageFiles.length > remainingSlots) {
    toast.warning(`Chỉ được đính kèm tối đa 6 ảnh`);
  }
  for (const file of imageFiles.slice(0, Math.max(0, remainingSlots))) {
    const validation = validateUploadFile(file, MAX_MEDIA_IMAGE_SIZE, label);
    if (!validation.valid) {
      toast.error(validation.error);
      continue;
    }
    try {
      const { fileUrl } = await uploadApi.uploadFile(file);
      urls.push(fileUrl);
    } catch (err) {
      toast.error("Không thể tải ảnh lên: " + extractErrorMessage(err));
    }
  }
  return urls;
}

interface ImagePickerButtonsProps {
  onFiles: (files: File[]) => void | Promise<void>;
  disabled?: boolean;
  isUploading?: boolean;
  testIdPrefix: string;
  /** Nhãn nút trên desktop */
  desktopLabel?: string;
  className?: string;
}

/**
 * Mobile/cảm ứng: "Chụp ảnh" (camera sau) + "Chọn từ thư viện".
 * Desktop: "Chọn ảnh" (kèm dán Ctrl+V / kéo thả do component cha xử lý).
 */
export function FeedbackImagePickerButtons({
  onFiles,
  disabled,
  isUploading,
  testIdPrefix,
  desktopLabel = "Chọn ảnh",
  className,
}: ImagePickerButtonsProps) {
  const isTouch = useIsTouchDevice();
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) await onFiles(files);
  };

  const btn =
    "inline-flex items-center justify-center gap-1.5 min-h-11 sm:min-h-9 px-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer";

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid={`${testIdPrefix}-camera-input`}
        onChange={handleChange}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
        onChange={handleChange}
      />
      {isTouch && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => cameraRef.current?.click()}
          data-testid={`${testIdPrefix}-camera-btn`}
          className={cn(btn, "flex-1 bg-blue-600 border-blue-600 text-white hover:bg-blue-700")}
        >
          <Camera className="h-4 w-4" /> Chụp ảnh
        </button>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => galleryRef.current?.click()}
        data-testid={`${testIdPrefix}-gallery-btn`}
        className={cn(btn, isTouch && "flex-1", "bg-white border-slate-300 text-slate-700 hover:bg-slate-50")}
      >
        {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        {isTouch ? "Chọn từ thư viện" : desktopLabel}
      </button>
    </div>
  );
}

interface ThumbGridProps {
  images: string[];
  onRemove?: (index: number) => void;
  onZoom?: (url: string) => void;
  isUploading?: boolean;
  /** Lưới 2 cột mobile / 3 cột desktop */
  className?: string;
  testId?: string;
  badgeFirst?: string;
}

export function FeedbackThumbGrid({
  images,
  onRemove,
  onZoom,
  isUploading,
  className,
  testId,
  badgeFirst,
}: ThumbGridProps) {
  if (!images.length && !isUploading) return null;
  return (
    <div data-testid={testId} className={cn("grid grid-cols-2 sm:grid-cols-3 gap-2", className)}>
      {images.map((url, idx) => (
        <div
          key={`${url}-${idx}`}
          className="relative aspect-video rounded-lg border border-slate-200 overflow-hidden bg-slate-50"
        >
          <button
            type="button"
            className="block w-full h-full cursor-zoom-in"
            onClick={() => onZoom?.(url)}
            aria-label={`Phóng to ảnh ${idx + 1}`}
          >
            <img src={getUploadUrl(url)} alt={`Ảnh ${idx + 1}`} className="w-full h-full object-cover" />
          </button>
          {badgeFirst && idx === 0 && (
            <span className="absolute bottom-1 left-1 rounded bg-blue-600 px-1.5 text-[11px] font-semibold text-white pointer-events-none">
              {badgeFirst}
            </span>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(idx)}
              aria-label="Xóa ảnh"
              title="Xóa ảnh"
              className="absolute top-1 right-1 h-8 w-8 sm:h-7 sm:w-7 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {isUploading && (
        <div className="aspect-video rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-sm text-slate-500 gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> Đang tải...
        </div>
      )}
    </div>
  );
}

